import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  /** What to render if a child throws. Defaults to a built-in fallback card. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Label shown in the default fallback ("Overview Strom", "Marktpreise", …). */
  label?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Tab-scoped error boundary. Catches synchronous render-time exceptions in
 * any descendant so a bug in a single chart component never blanks the whole
 * app. The user can still switch to another tab; pressing "Erneut" remounts
 * the subtree.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface in the console so it can be inspected via DevTools in
    // production. Logging is intentionally lightweight; we don't ship a
    // telemetry pipeline yet.
    console.error("[ErrorBoundary] caught render error", error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 px-4 py-3 text-sm text-rose-900 dark:text-rose-200">
        <p className="font-medium">
          {this.props.label ? `Fehler in „${this.props.label}"` : "Fehler"}
        </p>
        <p className="mt-1 text-xs opacity-80 break-words">{error.message}</p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-2 rounded-md bg-rose-900 text-white px-2.5 py-1 text-xs font-medium hover:bg-rose-800"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }
}
